import "./App.css";
import "./design-system/tokens.css";
import "./design-system/flag";  /* sets data-ui-v2 attribute on <html> */
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Dashboard from "./sections/Dashboard";
import ProjectView from "./sections/ProjectView";
import { AuthProvider, useAuth } from "./context/AuthContext";
import LoginPage from "./views/LoginPage";
import AdminPage from "./views/AdminPage";
import CanvasPage from "./views/CanvasPage";
import RecipesPage from "./views/RecipesPage";
import RecipeDetailPage from "./views/RecipeDetailPage";
import RecipeComparePage from "./views/RecipeComparePage";
import AdminDebugPanel from "./views/AdminDebugPanel";
import AdminSpendPanel from "./views/AdminSpendPanel";
import DesignSystemPage from "./views/DesignSystemPage";
import PublicStoryboard from "./views/PublicStoryboard";
import IdeaToScriptWorkbench from "./views/IdeaToScriptWorkbench";
import RunwayAlephRecipe from "./views/RunwayAlephRecipe";
import EiPiGradeWorkbench from "./pages/EiPiGradeWorkbench";
import GradeRoomPage from "./views/GradeRoomPage";
import MotionRoomPage from "./views/MotionRoomPage";
import VideoEditAlephPage from "./views/VideoEditAlephPage";
import VideoEditOmniFlashPage from "./views/VideoEditOmniFlashPage";
import OmniFlashDirectPage from "./views/OmniFlashDirectPage";
import LLMStudioPage from "./views/LLMStudioPage";
import ImageStudioRecipe from "./views/ImageStudioRecipe";
import DriftOverviewPage from "./views/DriftOverviewPage";
import RefUploadPage from "./views/RefUploadPage";
import LoginOAuthPage from "./pages/LoginOAuthPage";
import { Loader2 } from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import FeedbackWidget from "./components/FeedbackWidget";
import ErrorBoundary from "./components/ErrorBoundary";

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
}

function AppRoutes() {
  const { isAuthenticated, loading, user } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B0F19] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#0EA5E9]" />
      </div>
    );
  }
  
  return (
    <>
    <Routes>
      <Route path="/share/storyboard/:token" element={<PublicStoryboard />} />
      <Route path="/ref-upload/:token" element={<RefUploadPage />} />
      <Route path="/login" element={isAuthenticated ? <Navigate to="/" replace /> : <LoginPage />} />
      <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/project/:projectId" element={<ProtectedRoute><ProjectView /></ProtectedRoute>} />
      <Route path="/project/:projectId/stage-0" element={<ProtectedRoute><IdeaToScriptWorkbench /></ProtectedRoute>} />
      <Route path="/canvas/:projectId" element={<ProtectedRoute><CanvasPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><AdminPage /></ProtectedRoute>} />
      <Route path="/admin/debug" element={<ProtectedRoute><AdminDebugPanel /></ProtectedRoute>} />
      <Route path="/admin/spend" element={<ProtectedRoute><AdminSpendPanel /></ProtectedRoute>} />
      <Route path="/admin/design-system" element={<ProtectedRoute><DesignSystemPage /></ProtectedRoute>} />
      <Route path="/recipes" element={<ProtectedRoute><RecipesPage /></ProtectedRoute>} />
      <Route path="/recipes/compare" element={<ProtectedRoute><RecipeComparePage /></ProtectedRoute>} />
      <Route path="/recipes/runway-aleph" element={<ProtectedRoute><RunwayAlephRecipe /></ProtectedRoute>} />
      <Route path="/recipes/eipigrade" element={<ProtectedRoute><EiPiGradeWorkbench /></ProtectedRoute>} />
      <Route path="/grade-room" element={<ProtectedRoute><GradeRoomPage /></ProtectedRoute>} />
      <Route path="/recipes/grade-room" element={<ProtectedRoute><GradeRoomPage /></ProtectedRoute>} />
      <Route path="/motion-room" element={<ProtectedRoute><MotionRoomPage /></ProtectedRoute>} />
      <Route path="/recipes/motion-room" element={<ProtectedRoute><MotionRoomPage /></ProtectedRoute>} />
      {/* VideoEdit Aleph 2 — cut-aware, keyframe-driven editor (custom_route in recipes.py) */}
      <Route path="/recipes/videoedit-aleph2" element={<ProtectedRoute><VideoEditAlephPage /></ProtectedRoute>} />
      {/* VideoEdit Omni Flash — Gemini-powered clone of Aleph 2 */}
      <Route path="/recipes/videoedit-omni-flash" element={<ProtectedRoute><VideoEditOmniFlashPage /></ProtectedRoute>} />
      <Route path="/recipes/omni-flash-direct" element={<ProtectedRoute><OmniFlashDirectPage /></ProtectedRoute>} />
      {/* LLM Studio — server-proxied chat for OpenAI + Anthropic */}
      <Route path="/llm-studio" element={<ProtectedRoute><LLMStudioPage /></ProtectedRoute>} />
      <Route path="/llm-studio/:convId" element={<ProtectedRoute><LLMStudioPage /></ProtectedRoute>} />
      {/* Image Studio — standalone gpt-image-2 batch generation (2026-06-25) */}
      <Route path="/recipes/image-studio" element={<ProtectedRoute><ImageStudioRecipe /></ProtectedRoute>} />
      <Route path="/edit-room" element={<ProtectedRoute><VideoEditAlephPage /></ProtectedRoute>} />
      <Route path="/drift-overview" element={<ProtectedRoute><DriftOverviewPage /></ProtectedRoute>} />
      <Route path="/login/oauth" element={<LoginOAuthPage />} />
      <Route path="/recipes/:slug" element={<ProtectedRoute><RecipeDetailPage /></ProtectedRoute>} />
    </Routes>
    {isAuthenticated && <FeedbackWidget userEmail={user?.email} />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
        </AuthProvider>
        <Toaster position="top-right" theme="dark" richColors />
      </ErrorBoundary>
    </div>
  );
}

export default App;
